import React, { useCallback, useEffect } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { GREY } from '~/configs/colours';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import useAccommodationPropertyDetailsContext from '~/components/Accommodation/Pages/PropertyDetails/context/useAccommodationPropertyDetailsContext';
import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';

import { patchProperty } from '~/services/AccommodationService';

interface PropertyNameFormData {
  name: string;
}

interface Props {
  variant?: 'classic' | 'sidebar';
}

function SidebarReadOnlyField({ label, value, onCopy }: { label: string; value: string; onCopy?: () => void }) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: GREY[200], pb: 1 }}>
      <Typography variant="body2" color="text.secondary" mb={0.75}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1" sx={{ overflowWrap: 'anywhere', flex: 1 }}>
          {value}
        </Typography>
        {onCopy && (
          <IconButton aria-label={`Copy ${label}`} onClick={onCopy} size="small">
            <ContentCopyOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

export default function AccommodationPropertyDetailsOverviewPane({ variant = 'classic' }: Props) {
  const { propertyDetails, setPropertyDetails } = useAccommodationPropertyDetailsContext();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { control, handleSubmit, watch, reset } = useForm<PropertyNameFormData>({
    defaultValues: {
      name: propertyDetails.name || '',
    },
  });

  const currentName = watch('name');
  const hasChanges = currentName !== propertyDetails.name;

  // Reset form when propertyDetails changes
  useEffect(() => {
    reset({
      name: propertyDetails.name || '',
    });
  }, [propertyDetails.name, reset]);

  const updatePropertyMutation = useMutation({
    mutationFn: async (data: PropertyNameFormData) => {
      return await patchProperty({ propertyId: propertyDetails.id }, data);
    },
    onSuccess: (response) => {
      const updatedProperty = response.result;
      setPropertyDetails(updatedProperty);
      queryClient.invalidateQueries({ queryKey: ['property', propertyDetails.id] });
      enqueueSnackbar('Property name updated successfully', { variant: 'success' });
      reset({ name: updatedProperty.name });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating property name: ${error.message}`, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    (data: PropertyNameFormData) => {
      updatePropertyMutation.mutate(data);
    },
    [updatePropertyMutation],
  );

  const copyPropertyId = useCallback(() => {
    navigator.clipboard.writeText(propertyDetails.id);
    enqueueSnackbar('Copied ID to clipboard', { variant: 'success', autoHideDuration: 3500 });
  }, [enqueueSnackbar, propertyDetails.id]);

  if (variant === 'classic') {
    return (
      <Stack direction="column" gap={2}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: 'repeat(5, 1fr)' }}>
            <Box gridColumn={{ xs: '1', md: 'span 2' }}>
              <DefinitionTitle>Name</DefinitionTitle>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={updatePropertyMutation.isPending}
                  />
                )}
              />
            </Box>
            <Box>
              <DefinitionTitle>Chain Name</DefinitionTitle>
              <DefinitionText>{propertyDetails.chainName || '-'}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>Category</DefinitionTitle>
              <DefinitionText>{propertyDetails.category}</DefinitionText>
            </Box>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="flex-start">
              {hasChanges && (
                <Button type="submit" variant="contained" color="primary" disabled={updatePropertyMutation.isPending}>
                  {updatePropertyMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              )}
            </Box>

            <Box gridColumn={{ xs: '1', md: 'span 2' }}>
              <DefinitionTitle>ID</DefinitionTitle>
              <DefinitionText>{propertyDetails.id}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>Country</DefinitionTitle>
              <DefinitionText>{propertyDetails.country || 'Unknown'}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>City</DefinitionTitle>
              <DefinitionText>{propertyDetails.city || 'Unknown'}</DefinitionText>
            </Box>
            <Box>
              <DefinitionTitle>Rating</DefinitionTitle>
              <DefinitionText>{propertyDetails.rating}</DefinitionText>
            </Box>
          </Box>
        </form>
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
      <Accordion
        defaultExpanded
        sx={{
          border: 1,
          borderColor: 'divider',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="primary" />}
          sx={{
            bgcolor: 'primary.lightest',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Property data</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="column" gap={2.5}>
              <Box>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      variant="standard"
                      size="small"
                      fullWidth
                      disabled={updatePropertyMutation.isPending}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: 'text.secondary',
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <SidebarReadOnlyField label="ID" value={propertyDetails.id} onCopy={copyPropertyId} />
              <SidebarReadOnlyField label="Chain Name" value={propertyDetails.chainName || '-'} />
              <SidebarReadOnlyField label="Category" value={propertyDetails.category || '-'} />
              <SidebarReadOnlyField label="Country" value={propertyDetails.country || 'Unknown'} />
              <SidebarReadOnlyField label="City" value={propertyDetails.city || 'Unknown'} />
              <SidebarReadOnlyField
                label="Rating"
                value={propertyDetails.rating ? String(propertyDetails.rating) : '-'}
              />

              <Box display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!hasChanges || updatePropertyMutation.isPending}
                >
                  {updatePropertyMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
